import { forwardRef, useEffect, useRef } from 'react';
import Hls from 'hls.js';
import Player from './Player';

/**
 * HLS live player with DVR: joins at live edge, allows seeking back within the buffer.
 */
const HlsPlayer = forwardRef(function HlsPlayer(
  {
    src,
    poster,
    controls = true,
    muted = false,
    autoPlay = false,
    liveEdgeOnJoin = true,
    wrapClassName = 'Elokuva-player-wrap EventLivestream-hls-wrap',
    videoClassName = 'Elokuva-video EventLivestream-hls-video',
    ...rest
  },
  ref,
) {
  const internalRef = useRef(null);
  const videoRef = ref || internalRef;
  const hlsRef = useRef(null);
  const snappedToLiveRef = useRef(false);

  useEffect(() => {
    snappedToLiveRef.current = false;
  }, [src]);

  useEffect(() => {
    const video = videoRef?.current;
    if (!video || !src) return undefined;

    let hls = null;

    const snapToLiveEdge = () => {
      if (!liveEdgeOnJoin || snappedToLiveRef.current || !hls || !video) return;
      const liveEdge = hls.liveSyncPosition;
      if (Number.isFinite(liveEdge) && liveEdge > 0) {
        video.currentTime = Math.max(0, liveEdge - 1);
        snappedToLiveRef.current = true;
      }
    };

    if (Hls.isSupported()) {
      hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 600,
        liveBackBufferLength: 600,
        maxBufferLength: 120,
        maxMaxBufferLength: 7200,
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: 10,
        maxLiveSyncPlaybackRate: 1.25,
      });
      hlsRef.current = hls;
      hls.loadSource(src);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (autoPlay) video.play().catch(() => {});
      });
      hls.on(Hls.Events.LEVEL_LOADED, (_event, data) => {
        if (data?.details?.live) snapToLiveEdge();
      });
      hls.on(Hls.Events.FRAG_BUFFERED, () => {
        snapToLiveEdge();
      });
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) {
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            hls.startLoad();
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
          }
        }
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      if (autoPlay) video.play().catch(() => {});
    }

    return () => {
      if (hls) {
        hls.destroy();
        hlsRef.current = null;
      }
    };
  }, [src, videoRef, autoPlay, liveEdgeOnJoin]);

  return (
    <Player
      ref={videoRef}
      src={Hls.isSupported() ? undefined : src}
      poster={poster}
      controls={controls}
      muted={muted}
      autoPlay={autoPlay}
      wrapClassName={wrapClassName}
      videoClassName={videoClassName}
      {...rest}
    />
  );
});

HlsPlayer.displayName = 'HlsPlayer';

export default HlsPlayer;
